import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BrandLogo } from '../lib/branding';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * VerifyEmailPage — Page de saisie du code de vérification email (6 chiffres)
 * 
 * Affiché après l'inscription. L'utilisateur saisit le code reçu par email.
 * Après validation: token JWT retourné → accès plateforme.
 */
const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginDirect } = useAuth();
  
  const email = location.state?.email || '';
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  
  const inputRefs = useRef([]);

  // Redirect if no email in state
  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  // Cooldown timer for resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Handle digit input
  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError('');
    
    // Auto-focus next
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  // Submit verification
  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Veuillez saisir les 6 chiffres du code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: fullCode })
      });
      const data = await res.json();
      
      if (!res.ok) {
        setError(data.detail || 'Code incorrect');
        return;
      }

      // Login automatique avec le token retourné
      if (data.token && data.user) {
        loginDirect(data.token, data.user);
        toast.success('Email vérifié avec succès !');
        navigate('/dashboard');
      }
    } catch {
      setError('Erreur serveur. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Resend code
  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        toast.success('Un nouveau code a été envoyé');
        setCooldown(60);
        setCode(['', '', '', '', '', '']);
        setError('');
        inputRefs.current[0]?.focus();
      }
    } catch {
      toast.error('Erreur lors du renvoi');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center p-4" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <BrandLogo size="lg" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Vérifiez votre adresse email professionnel
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Un code de vérification à 6 chiffres a été envoyé à
          </p>
          <p className="text-sm font-medium text-primary mt-1">{email}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-center block text-sm text-muted-foreground">
                Saisissez le code reçu par email
              </Label>
              
              {/* 6-digit code input */}
              <div className="flex justify-center gap-2" onPaste={handlePaste}>
                {code.map((digit, i) => (
                  <Input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    className="w-12 h-14 text-center text-xl font-bold"
                    data-testid={`verify-code-input-${i}`}
                    autoFocus={i === 0}
                  />
                ))}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="text-sm text-red-600 text-center bg-red-50 rounded-lg p-3" data-testid="verify-error">
                {error}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              className="w-full"
              disabled={loading || code.join('').length !== 6}
              data-testid="verify-submit-button"
            >
              {loading ? 'Vérification...' : 'Valider'}
            </Button>
          </form>

          {/* Resend */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              className="text-sm text-primary hover:underline disabled:text-gray-400 disabled:no-underline"
              data-testid="resend-code-button"
            >
              {cooldown > 0
                ? `Renvoyer un code (${cooldown}s)`
                : resending
                  ? 'Envoi en cours...'
                  : 'Renvoyer un code'}
            </button>
          </div>
        </div>

        {/* Back to login */}
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-sm text-muted-foreground hover:underline"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
